/**
 * TTSStatusBadge — admin header indicator showing TTS upstream health.
 *
 * Polls `/api/tts/voices` every 60s. Renders a small pill:
 *   • Green dot + "TTS · N voices"        — upstream online, list fetched
 *   • Amber dot + "TTS · checking…"       — initial load
 *   • Red dot + "TTS · offline"           — upstream unreachable
 *
 * Click → opens a tooltip-style popover with diagnostic detail and a
 * "Recheck now" button so the admin can force a refresh after
 * restarting their tunnel.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 60_000;

const TTSStatusBadge = () => {
  const [state, setState] = useState({
    status: 'loading',          // 'loading' | 'online' | 'offline'
    voices: [],
    error: null,
    lastChecked: null,
  });
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/tts/voices`);
      if (!r.ok) {
        setState({ status: 'offline', voices: [], error: `HTTP ${r.status}`, lastChecked: new Date() });
        return;
      }
      const d = await r.json();
      const list = Array.isArray(d.voices) ? d.voices : [];
      // Treat "only default voice + upstream_offline flag" as offline.
      const offline = Boolean(d.upstream_offline);
      setState({
        status: offline ? 'offline' : 'online',
        voices: list,
        error: offline ? (d.upstream_error || 'Upstream unreachable') : null,
        lastChecked: new Date(),
      });
    } catch (err) {
      setState({ status: 'offline', voices: [], error: err.message || 'Network error', lastChecked: new Date() });
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(id);
  }, [fetchStatus]);

  // Click-outside to dismiss the popover.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const dotClass =
    state.status === 'online'
      ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]'
      : state.status === 'offline'
      ? 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.7)]'
      : 'bg-amber-300 animate-pulse';

  const label =
    state.status === 'online'
      ? `TTS · ${state.voices.length} voice${state.voices.length === 1 ? '' : 's'}`
      : state.status === 'offline'
      ? 'TTS · offline'
      : 'TTS · checking…';

  const borderClass =
    state.status === 'online'
      ? 'border-emerald-500/40 hover:border-emerald-400'
      : state.status === 'offline'
      ? 'border-rose-500/50 hover:border-rose-400'
      : 'border-amber-400/40 hover:border-amber-300';

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-full border bg-slate-900/70 px-2.5 py-1 text-xs font-semibold text-slate-200 transition-colors ${borderClass} focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300`}
        aria-expanded={open}
        aria-label={`TTS status: ${label}`}
        data-testid="tts-status-badge"
        data-status={state.status}
      >
        <span className={`inline-block w-2 h-2 rounded-full ${dotClass}`} aria-hidden="true" />
        <span>{label}</span>
      </button>

      {open && (
        <div
          className="absolute left-0 sm:right-0 sm:left-auto mt-2 w-[calc(100vw-2rem)] max-w-xs rounded-xl border border-slate-700 bg-slate-900/95 backdrop-blur-md shadow-2xl p-3 z-50 text-xs space-y-2"
          data-testid="tts-status-popover"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-bold text-white text-sm">TTS Server Status</p>
              <p
                className={
                  state.status === 'online'
                    ? 'text-emerald-300'
                    : state.status === 'offline'
                    ? 'text-rose-300'
                    : 'text-amber-300'
                }
              >
                {state.status === 'online'
                  ? `Online · ${state.voices.length} voice${state.voices.length === 1 ? '' : 's'} available`
                  : state.status === 'offline'
                  ? 'Offline — your tunnel is not reachable'
                  : 'Checking upstream…'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setState((s) => ({ ...s, status: 'loading' })); fetchStatus(); }}
              className="shrink-0 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-bold px-2 py-1"
              data-testid="tts-status-recheck"
            >
              Recheck
            </button>
          </div>

          {state.status === 'online' && state.voices.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Voices</p>
              <div className="flex flex-wrap gap-1">
                {state.voices.map((v) => (
                  <span
                    key={v}
                    className="rounded-full bg-slate-800 border border-slate-700 text-slate-200 px-2 py-0.5 text-[11px]"
                  >
                    {v}
                  </span>
                ))}
              </div>
            </div>
          )}

          {state.status === 'offline' && (
            <div className="rounded-md border border-rose-500/40 bg-rose-950/40 p-2">
              <p className="text-rose-200 font-mono text-[11px] break-all">{state.error || 'Upstream unreachable'}</p>
              <p className="text-rose-200/80 mt-1.5 text-[11px]">
                Restart <code className="font-mono">cloudflared</code> and ensure
                <code className="font-mono"> tts_service_url.json </code>
                on R2 points to the live tunnel.
              </p>
            </div>
          )}

          {state.lastChecked && (
            <p className="text-[10px] text-slate-500 text-right">
              Last checked: {state.lastChecked.toLocaleTimeString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TTSStatusBadge;
